import { useEffect, useState, useRef } from 'react'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import clsx from 'clsx'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import DistanceIcon from '@mui/icons-material/Straighten'
import AzimuthIcon from '@mui/icons-material/NavigationOutlined'
import SlickCarousel from '../../control/SlickCarousel'
import Button from '../../control/Button'
import { ReactComponent as CellularIcon } from '../../icon/cellular.svg'
import { useTranslation } from 'react-i18next'

type MapResultProps = {
    data?: SearchDataType | null
    stationNumber: number
    onClickResult?: (item: StationType) => void
    onHilightSelectStation: (itemSelectedNo: number) => void
    resetSearchData: () => void
}
const MapResultWeb = (props: MapResultProps) => {
    const { data, stationNumber, onClickResult, onHilightSelectStation, resetSearchData } = props
    const classes = useStyles()
    const { t } = useTranslation()

    const [result, setResult] = useState<StationType[]>([])
    const [resultActive, setResultActive] = useState<number>(1)

    useEffect(() => {
        if (data && data.stations?.length) {
            setResult(data.stations)
        } else {
            setResult([])
        }
    }, [data])

    useEffect(() => {
        if (stationNumber) {
            setResultActive(stationNumber)
        }
    }, [stationNumber])

    const onSelectResult = (itemSelected?: StationType) => {
        const res = result.map((item, index) => {
            if (!item.no) item.no = index + 1
            if (item.station_id === itemSelected?.station_id) item.activeResult = true
            else item.activeResult = false
            return item
        })
        setResult(res)
        if (itemSelected && itemSelected.no) {
            onClickResult?.(itemSelected)
            onHilightSelectStation(itemSelected.no as number)
        }
    }

    const afterChange = (index: number) => {
        const dataResult = result?.[index]
        const num = dataResult?.no ?? 1
        setResultActive(num)
        onHilightSelectStation(num)
    }

    const settings = {
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 3,
        arrows: false,
        draggable: true,
        afterChange: afterChange,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    dots: false,
                    slidesToShow: 2,
                    slidesToScroll: 2,
                    arrows: false,
                    draggable: true,
                },
            },
            {
                breakpoint: 720,
                settings: {
                    dots: false,
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false,
                    draggable: true,
                },
            },
        ],
    }

    return (
        <>
            {result.length === 0 && (
                <Stack direction="row" justifyContent="center" alignItems="center" className={classes.noData}>
                    {t('LABEL.MAP.MAP_RESULT.NO_RESULT')}
                </Stack>
            )}
            {result.length > 0 && (
                <Grid container className={classes.containerPanel}>
                    <Grid item xs={12}>
                        <Typography className={classes.labelName} sx={{ pl: '10px', py: 1 }}>
                            {t('LABEL.MAP.MAP_RESULT.TITLE')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <SlickCarousel settings={settings}>
                            {result.map((item, index) => {
                                return (
                                <Grid container key={index}>
                                    <Grid item xs={12}>
                                        <Paper
                                            className={clsx(classes.paper, {
                                                [classes.paperActive]: item.no === resultActive,
                                            })}
                                        >
                                            <Stack spacing={1}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    spacing={1}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <Paper className={classes.labelNo}>
                                                            <Typography className={classes.labelName}>
                                                                {item.no}
                                                            </Typography>
                                                        </Paper>
                                                        <div style={{ marginLeft: '15px' }}>
                                                            <StyledTypoStationNameTH className={classes.lineClamp}>
                                                                {item?.name_th}
                                                            </StyledTypoStationNameTH>
                                                        </div>
                                                    </div>
                                                    <StyledButtonView
                                                        color="primary"
                                                        onClick={() => onSelectResult(item)}
                                                    >
                                                        {t('LABEL.MAP.MAP_RESULT.VIEW')}
                                                    </StyledButtonView>
                                                </Stack>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <StyledIconCellular />
                                                    <StyledTypoContent>{item.station_type}</StyledTypoContent>
                                                </Stack>
                                                {item.distance !== undefined && item.distance !== null && item.azimuth !== undefined && item.azimuth !== null && (
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <StyledIconDistance />
                                                            <StyledTypoContent>{item.distance}</StyledTypoContent>
                                                        </Stack>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <StyledIconAzimuth />
                                                            <StyledTypoContent>
                                                                {`${t('LABEL.MAP.MAP_RESULT.ANGLE_RELATIVE')} ${
                                                                    item.azimuth?.toFixed(0) + '°' || '-'
                                                             
                                                                }`}
                                                            </StyledTypoContent>
                                                        </Stack>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )})}
                        </SlickCarousel>
                    </Grid>
                </Grid>
            )}
        </>
    )
}

export default MapResultWeb

const useStyles = makeStyles((theme: Theme) => ({
    noData: {
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.primary.main,
        opacity: 0.8,
        height: '200px',
        marginTop: '0px !important',
    },
    containerPanel: {
        padding: '8px 18px 18px 18px',
        backgroundColor: 'rgba(49, 138, 241, 0.8)',
        marginTop: '0px !important',
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        paddingTop: '0px !important',
    },
    floatLeft: {
        float: 'left',
        marginLeft: theme.spacing(2),
    },
    floatRight: {
        float: 'right',
        marginRight: theme.spacing(2),
    },
    subtitle1: {
        display: 'flex',
        justifyContent: 'center',
    },
    subtitle2: {
        display: 'flex',
        justifyContent: 'center',
    },
    paper: {
        padding: theme.spacing(2),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `#fff`,
        boxShadow: `${theme.palette.getContrastText('shadow-box')} !important`,
        borderRadius: '10px !important',
        height: '83px',
        border: `1px solid ${theme.palette.primary.main}`,
    },
    paperActive: {
        padding: theme.spacing(2),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        color: `${theme.palette.text.secondary} !important`,
        backgroundColor: `#fff`,
        boxShadow: `${theme.palette.getContrastText('shadow-box')} !important`,
        borderRadius: '10px !important',
        height: '80px',
        border: `3px solid ${theme.palette.getContrastText('green')}`,
    },
    label: {
        color: `${theme.palette.text.secondary} !important`,
    },
    labelActive: {
        color: `${theme.palette.secondary.dark} !important`,
    },
    labelLocation: {
        color: `${theme.palette.text.secondary} !important`,
    },
    labelLocationActive: {
        color: `${theme.palette.text.disabled} !important`,
    },
    divider: {
        background: `${theme.palette.text.secondary} !important`,
    },
    dividerActive: {
        background: `${theme.palette.text.primary} !important`,
    },
    icon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    iconActive: {
        color: `${theme.palette.secondary.dark} !important`,
    },
    iconLocation: {
        color: `${theme.palette.text.secondary} !important`,
    },
    iconLocationActive: {
        color: `${theme.palette.text.disabled} !important`,
    },
    labelNo: {
        backgroundColor: `${theme.palette.primary.main} !important`,
        borderRadius: '4px',
        minWidth: '25px',
        minHeight: '25px',
        textAlign: 'center',
    },
    labelName: {
        color: '#fff',
        fontSize: '14px',
        marginTop: '1px',
    },
    lineClamp: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
    },
}))

const StyledTypoStationNameTH = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.dark,
    fontSize: '16px',
    fontWeight: 600,
    flexGrow: 2,
}))

const StyledTypoContent = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '14px',
}))

const StyledButtonView = styled(Button)(({ theme }) => ({
    width: '90px',
    height: '25px',
}))

const StyledIconDistance = styled(DistanceIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}))

const StyledIconAzimuth = styled(AzimuthIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}))

const StyledIconCellular = styled(CellularIcon)(({ theme }) => ({
    width: '16px',
    marginLeft: '3px',
}))
